/**
 * Work-order policy envelopes admin API module — the account-default /
 * location-value endpoints for the work-order checks (VIN duplicate, service
 * duplicate, override PIN) and the location side of the edit lock.
 *
 * Every GET returns the flat effective fields + `sources` (+ `overrides` and
 * `account` on location GETs). PATCH bodies are tri-state: absent = untouched,
 * null = clear the cell, value = set. See components/policy/policyEnvelope.ts.
 *
 * The account edit-lock GET/PUT stays in usersRoles.ts (unchanged contract —
 * its response now also carries `sources`, which the flat type tolerates).
 */
import { webApiClient } from '../apiClient';
import { dedup } from './helpers';
import { toPolicyApiError } from './policyErrors';
import type {
  EditLockPatch,
  EditLockPolicyEnvelope,
  WorkOrderCheckAccountPatch,
  WorkOrderCheckPatch,
  WorkOrderCheckPolicyEnvelope,
} from '../../components/policy/policyEnvelope';

// Errors carry the backend code (409 POLICY_FIELD_LOCKED gets its own
// wording); the ApiClient flattens the body, so this reads `errorBody`.
const fail = (res: { error?: string; errorBody?: any; status?: number }, fallback: string) => toPolicyApiError(res, fallback);

const checkBase = (accountId: string) => `/accounts/${accountId}/work-order-check-policy`;
const locationCheckBase = (accountId: string, locationId: string) =>
  `/accounts/${accountId}/locations/${locationId}/work-order-check-policy`;
const locationEditLockBase = (accountId: string, locationId: string) =>
  `/accounts/${accountId}/locations/${locationId}/work-order-edit-lock-policy`;

// ── Account: work-order checks ──────────────────────────────────────────────

export async function getWorkOrderCheckPolicy(accountId: string): Promise<WorkOrderCheckPolicyEnvelope> {
  return dedup(`getWorkOrderCheckPolicy:${accountId}`, async () => {
    const res = await webApiClient.get<WorkOrderCheckPolicyEnvelope>(checkBase(accountId));
    if (!res.success) throw fail(res, 'Failed to fetch work order check settings');
    return res.data as WorkOrderCheckPolicyEnvelope;
  });
}

/** Account PATCH: changed fields plus `locked_fields` when the lock list changed. */
export async function updateWorkOrderCheckPolicy(
  accountId: string,
  patch: WorkOrderCheckAccountPatch
): Promise<WorkOrderCheckPolicyEnvelope> {
  const res = await webApiClient.patch<WorkOrderCheckPolicyEnvelope>(checkBase(accountId), patch);
  if (!res.success) throw fail(res, 'Failed to update work order check settings');
  return res.data as WorkOrderCheckPolicyEnvelope;
}

export async function setAccountOverridePIN(accountId: string, pin: string): Promise<void> {
  const res = await webApiClient.put<void>(`${checkBase(accountId)}/override-pin`, { pin });
  if (!res.success) throw fail(res, 'Failed to set the account PIN');
}

export async function clearAccountOverridePIN(accountId: string): Promise<void> {
  const res = await webApiClient.delete<void>(`${checkBase(accountId)}/override-pin`);
  if (!res.success) throw fail(res, 'Failed to clear the account PIN');
}

// ── Location: work-order checks ─────────────────────────────────────────────

export async function getLocationWorkOrderCheckPolicy(accountId: string, locationId: string): Promise<WorkOrderCheckPolicyEnvelope> {
  return dedup(`getLocationWorkOrderCheckPolicy:${accountId}:${locationId}`, async () => {
    const res = await webApiClient.get<WorkOrderCheckPolicyEnvelope>(locationCheckBase(accountId, locationId));
    if (!res.success) throw fail(res, 'Failed to fetch work order check settings');
    return res.data as WorkOrderCheckPolicyEnvelope;
  });
}

export async function updateLocationWorkOrderCheckPolicy(
  accountId: string,
  locationId: string,
  patch: WorkOrderCheckPatch
): Promise<WorkOrderCheckPolicyEnvelope> {
  const res = await webApiClient.patch<WorkOrderCheckPolicyEnvelope>(locationCheckBase(accountId, locationId), patch);
  if (!res.success) throw fail(res, 'Failed to update work order check settings');
  return res.data as WorkOrderCheckPolicyEnvelope;
}

/** Follow the account everywhere, INCLUDING the PIN. */
export async function resetLocationWorkOrderCheckPolicy(accountId: string, locationId: string): Promise<WorkOrderCheckPolicyEnvelope> {
  const res = await webApiClient.delete<WorkOrderCheckPolicyEnvelope>(locationCheckBase(accountId, locationId));
  if (!res.success) throw fail(res, 'Failed to reset work order check settings');
  return res.data as WorkOrderCheckPolicyEnvelope;
}

export async function setLocationOverridePIN(accountId: string, locationId: string, pin: string): Promise<void> {
  const res = await webApiClient.put<void>(`${locationCheckBase(accountId, locationId)}/override-pin`, { pin });
  if (!res.success) throw fail(res, 'Failed to set the location PIN');
}

/** Use the account PIN at this location. */
export async function clearLocationOverridePIN(accountId: string, locationId: string): Promise<void> {
  const res = await webApiClient.delete<void>(`${locationCheckBase(accountId, locationId)}/override-pin`);
  if (!res.success) throw fail(res, 'Failed to clear the location PIN');
}

// ── Location: edit lock ─────────────────────────────────────────────────────

export async function getLocationWorkOrderEditLockPolicy(accountId: string, locationId: string): Promise<EditLockPolicyEnvelope> {
  return dedup(`getLocationWorkOrderEditLockPolicy:${accountId}:${locationId}`, async () => {
    const res = await webApiClient.get<EditLockPolicyEnvelope>(locationEditLockBase(accountId, locationId));
    if (!res.success) throw fail(res, 'Failed to fetch the edit-lock settings');
    return res.data as EditLockPolicyEnvelope;
  });
}

export async function updateLocationWorkOrderEditLockPolicy(
  accountId: string,
  locationId: string,
  patch: EditLockPatch
): Promise<EditLockPolicyEnvelope> {
  const res = await webApiClient.patch<EditLockPolicyEnvelope>(locationEditLockBase(accountId, locationId), patch);
  if (!res.success) throw fail(res, 'Failed to update the edit-lock settings');
  return res.data as EditLockPolicyEnvelope;
}

/** Follow the account everywhere. */
export async function resetLocationWorkOrderEditLockPolicy(accountId: string, locationId: string): Promise<EditLockPolicyEnvelope> {
  const res = await webApiClient.delete<EditLockPolicyEnvelope>(locationEditLockBase(accountId, locationId));
  if (!res.success) throw fail(res, 'Failed to reset the edit-lock settings');
  return res.data as EditLockPolicyEnvelope;
}

export const workOrderPoliciesAdmin = {
  getWorkOrderCheckPolicy,
  updateWorkOrderCheckPolicy,
  setAccountOverridePIN,
  clearAccountOverridePIN,
  getLocationWorkOrderCheckPolicy,
  updateLocationWorkOrderCheckPolicy,
  resetLocationWorkOrderCheckPolicy,
  setLocationOverridePIN,
  clearLocationOverridePIN,
  getLocationWorkOrderEditLockPolicy,
  updateLocationWorkOrderEditLockPolicy,
  resetLocationWorkOrderEditLockPolicy,
};
